import * as React from 'react';
import { useTheme } from '@emotion/react';
import { motion } from 'motion/react';
import { STORY_APPS, StoryScene } from '../story';
import { S } from '../styles';

const APP_WORKFLOW_PATH = 'M116 90 H140 Q150 90 150 80 V57 Q150 47 160 47 H174 M252 74 V120';

interface AppFleetArtworkProps {
  scene: StoryScene;
  grouped: boolean;
  reducedMotion: boolean;
  animateConnections: boolean;
}

export const AppFleetArtwork: React.FC<AppFleetArtworkProps> = ({
  scene,
  grouped,
  reducedMotion,
  animateConnections
}) => {
  const theme = useTheme();
  const colors = theme.featureGraph;
  const transition = { duration: reducedMotion ? 0 : 0.7 };
  const reveal = (firstScene: StoryScene) => ({ opacity: scene >= firstScene ? 1 : 0 });

  return (
    <motion.g initial={false} animate={reveal(StoryScene.ADD_APPS)} transition={transition}>
      {STORY_APPS.map((app) => (
        <motion.g
          key={app.title}
          initial={false}
          animate={reveal(app.original ? StoryScene.MULTIPLE_APPS : StoryScene.ADD_APPS)}
          transition={transition}
        >
          <g
            style={{
              transform:
                grouped || app.original
                  ? `translate(${app.x}px, 265px)`
                  : `translate(-420px, ${app.x === 480 ? 70 : 460}px)`,
              transformOrigin: '0px 0px',
              transformBox: 'view-box',
              transition: reducedMotion ? 'none' : 'transform 1.6s cubic-bezier(0.22, 1, 0.36, 1)'
            }}
          >
            <rect
              width="380"
              height="330"
              rx="24"
              fill={theme.surface.background}
              stroke={colors.runtime.border}
              strokeWidth="2"
            />
            <rect width="380" height="330" rx="24" fill={colors.runtime.background} />
            <text x="24" y="34" fill={colors.runtime.accent} fontSize="11" letterSpacing="2">
              {app.kind}
            </text>
            <text x="24" y="76" fill={theme.text.heading} fontSize="25" fontWeight="600">
              {app.title}
            </text>
            <text x="24" y="105" fill={theme.text.paragraph} fontSize="14">
              {app.detail}
            </text>
            {!app.original ? (
              <g transform="translate(24 130) scale(1 0.9)">
                <rect
                  width="116"
                  height="180"
                  rx="14"
                  fill={theme.surface.background}
                  stroke={colors.runtime.accent}
                  strokeWidth="1.5"
                />
                <rect x="14" y="22" width="88" height="32" rx="5" fill={colors.runtime.background} />
                <text x="23" y="43" fill={theme.text.heading} fontSize="12">
                  {app.x === 480 ? 'Stock intake' : 'Work orders'}
                </text>
                {[75, 100, 125].map((y) => (
                  <g key={y}>
                    <rect x="14" y={y} width="88" height="17" rx="4" fill={theme.card.background} />
                    <path d={`M22 ${y + 8} h45`} stroke={colors.runtime.muted} />
                  </g>
                ))}
                <path d={APP_WORKFLOW_PATH} fill="none" stroke={colors.cloudService.border} strokeWidth="2" />
                <S.Flow
                  d={APP_WORKFLOW_PATH}
                  running={animateConnections}
                  fill="none"
                  stroke={colors.cloudService.accent}
                  strokeWidth="2"
                  strokeDasharray="5 22"
                />
                <rect
                  x="174"
                  y="20"
                  width="156"
                  height="54"
                  rx="10"
                  fill={colors.cloudService.background}
                  stroke={colors.cloudService.border}
                />
                <text x="252" y="52" textAnchor="middle" fill={colors.cloudService.accent} fontSize="14">
                  {app.x === 480 ? 'Update inventory' : 'Assign work'}
                </text>
                <rect
                  x="174"
                  y="120"
                  width="156"
                  height="54"
                  rx="10"
                  fill={colors.feature.background}
                  stroke={colors.feature.border}
                />
                <text x="252" y="152" textAnchor="middle" fill={colors.feature.accent} fontSize="14">
                  {app.x === 480 ? 'Dispatch ready' : 'Team notified'}
                </text>
              </g>
            ) : null}
          </g>
        </motion.g>
      ))}
    </motion.g>
  );
};
